import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CategoryChips } from "./CategoryChips";
import { api, formatApiError } from "../lib/api";
import { RECIPE_CATEGORIES, euro } from "../lib/constants";
import { Search, X, ChefHat, Plus, Loader2, BookOpen, DownloadCloud, Lock, AlertTriangle, ListPlus } from "lucide-react";
import { toast } from "sonner";

export function RecipeCatalogDialog({ open, onOpenChange, onAdded }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [addingId, setAddingId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [showAvailableOnly, setShowAvailableOnly] = useState(false);
    // Risultato bulk import — dialog dei mancanti
    const [missingReport, setMissingReport] = useState(null); // { imported, skipped_existing, skipped_unimportable, missing_ingredients: [...] }

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/recipe-catalog")
            .then(({ data }) => setItems(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [open]);

    const filtered = useMemo(() => {
        let r = items;
        if (cat) r = r.filter((i) => i.category === cat);
        if (showAvailableOnly) r = r.filter((i) => i.available);
        if (q.trim()) {
            const s = q.toLowerCase();
            r = r.filter((i) => i.name.toLowerCase().includes(s));
        }
        return r;
    }, [items, q, cat, showAvailableOnly]);

    const availableCount = useMemo(
        () => items.filter((i) => i.available).length,
        [items]
    );

    const addToMenu = async (catalogId) => {
        setAddingId(catalogId);
        try {
            const { data } = await api.post(`/recipes/from-catalog/${catalogId}`);
            const missing = data.missing_ingredients || [];
            if (missing.length > 0) {
                toast.warning(
                    `Piatto aggiunto, ma ${missing.length} ingrediente/i non trovato/i: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`
                );
            } else {
                toast.success(`"${data.name}" aggiunto al tuo menu`);
            }
            onAdded?.(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setAddingId(null);
        }
    };

    const addMissingToList = async (items, recipeName) => {
        const list = items.filter(Boolean);
        if (list.length === 0) return;
        try {
            await api.post("/shopping-list/add", {
                items: list.map((n) => ({ name: n, recipe_name: recipeName })),
            });
            toast.success(`${list.length} ingredient${list.length === 1 ? "e aggiunto" : "i aggiunti"} alla lista mancanti`);
            onAdded?.({ shoppingListChanged: true });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const importAll = async () => {
        if (!window.confirm("Importare tutto il catalogo nel tuo menu? Le ricette già presenti con lo stesso nome verranno saltate. Quelle senza ingredienti compatibili verranno escluse.")) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/recipes/from-catalog/bulk");
            toast.success(
                `Importate ${data.imported} ricette` +
                (data.skipped_existing ? ` · ${data.skipped_existing} già presenti` : "") +
                (data.skipped_unimportable ? ` · ${data.skipped_unimportable} non importabili` : "")
            );
            // Mostra la lista degli ingredienti mancanti se presente
            if ((data.missing_ingredients || []).length > 0) {
                setMissingReport(data);
            } else {
                onOpenChange(false);
            }
            onAdded?.({ bulk: true });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="recipe-catalog-dialog"
                className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0 gap-0 bg-brand-cream"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-brand-line bg-paper">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center shrink-0">
                            <BookOpen size={20} />
                        </div>
                        <div className="flex-1">
                            <div className="eyebrow text-brand-gold mb-1">Catalogo ricette</div>
                            <DialogTitle className="font-display text-2xl text-brand-ink">
                                Scegli un piatto dal nostro ricettario
                            </DialogTitle>
                            <p className="text-sm text-brand-muted mt-1">
                                Oltre 200 ricette italiane pronte. Aggiungile una a una o importale tutte.
                            </p>
                            <button
                                type="button"
                                onClick={importAll}
                                disabled={bulkBusy}
                                data-testid="catalog-import-all"
                                className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider hover:bg-brand-goldDark transition disabled:opacity-60"
                            >
                                {bulkBusy ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <DownloadCloud size={14} />
                                )}
                                {bulkBusy ? "Importazione…" : "Importa tutto il catalogo"}
                            </button>
                        </div>
                    </div>
                </DialogHeader>

                <div className="px-6 pt-4 pb-3 bg-paper border-b border-brand-line space-y-3">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                        <input
                            data-testid="catalog-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cerca un piatto…"
                            className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                        {q && (
                            <button
                                onClick={() => setQ("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted"
                                aria-label="Pulisci"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <CategoryChips
                        value={cat}
                        onChange={setCat}
                        categories={RECIPE_CATEGORIES}
                        allLabel="Tutte"
                    />
                    <label className="flex items-center gap-2 text-[12px] text-brand-muted cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showAvailableOnly}
                            onChange={(e) => setShowAvailableOnly(e.target.checked)}
                            data-testid="catalog-only-available"
                            className="h-4 w-4 accent-brand-navy cursor-pointer"
                        />
                        <span>
                            Mostra solo i piatti realizzabili con la mia dispensa
                            {items.length > 0 && (
                                <span className="ml-1 font-bold text-brand-ink">
                                    ({availableCount}/{items.length})
                                </span>
                            )}
                        </span>
                    </label>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="catalog-results">
                    <div className="text-xs text-brand-muted mb-3">
                        {loading ? "Caricamento…" : `${filtered.length} ricette ${showAvailableOnly ? "realizzabili" : "in catalogo"}`}
                    </div>
                    {!loading && filtered.length === 0 && (
                        <div className="rounded-xl border border-dashed border-brand-line bg-paper p-10 text-center text-sm text-brand-muted">
                            Nessuna ricetta corrisponde alla ricerca.
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {filtered.map((r) => {
                            const isAvail = r.available;
                            const missing = r.missing_ingredients || [];
                            return (
                                <div
                                    key={r.id}
                                    data-testid={`catalog-item-${r.id}`}
                                    className={`rounded-xl border p-4 transition-colors ${
                                        isAvail
                                            ? "bg-paper border-brand-line hover:border-brand-gold/50"
                                            : "bg-brand-cream/40 border-brand-line/60 opacity-90"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="eyebrow text-brand-gold text-[10px]">{r.category}</div>
                                        {!isAvail && (
                                            <span
                                                className="inline-flex items-center gap-1 rounded-full bg-brand-warning/15 text-brand-warning border border-brand-warning/30 px-2 py-0.5 text-[9px] uppercase tracking-wider font-bold"
                                                title="Mancano ingredienti nella tua dispensa"
                                            >
                                                <Lock size={10} /> Non disponibile
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 flex items-start gap-2">
                                        <ChefHat size={16} className={`shrink-0 mt-1 ${isAvail ? "text-brand-muted" : "text-brand-muted/60"}`} />
                                        <h3 className={`font-display text-lg leading-tight flex-1 ${isAvail ? "text-brand-ink" : "text-brand-ink/70"}`}>
                                            {r.name}
                                        </h3>
                                    </div>
                                    <div className="mt-2 flex items-center gap-3 text-[12px] text-brand-muted flex-wrap">
                                        <span>{r.portions} porz.</span>
                                        <span className="h-1 w-1 rounded-full bg-brand-line" />
                                        <span>{r.ingredients_count} ingr.</span>
                                        {r.selling_price > 0 && (
                                            <>
                                                <span className="h-1 w-1 rounded-full bg-brand-line" />
                                                <span>Sugg. {euro(r.selling_price)}</span>
                                            </>
                                        )}
                                    </div>
                                    {!isAvail && missing.length > 0 && (
                                        <div
                                            className="mt-2 rounded-lg bg-brand-warning/8 border border-brand-warning/20 px-3 py-2 text-[11px] text-brand-ink/85"
                                            data-testid={`catalog-missing-${r.id}`}
                                        >
                                            <span className="text-[9px] uppercase tracking-wider font-bold text-brand-warning block mb-0.5">
                                                Ti mancano ({missing.length})
                                            </span>
                                            {missing.slice(0, 4).join(" · ")}
                                            {missing.length > 4 && ` · +${missing.length - 4} altri`}
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); addMissingToList(missing, r.name); }}
                                                data-testid={`catalog-add-missing-${r.id}`}
                                                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-warning text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition"
                                            >
                                                <ListPlus size={12} />
                                                Metti gli ingredienti mancanti in lista
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => addToMenu(r.id)}
                                        disabled={addingId === r.id || !isAvail}
                                        data-testid={`catalog-add-${r.id}`}
                                        className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                                            isAvail
                                                ? "bg-brand-navy text-white hover:bg-brand-navyLight"
                                                : "bg-brand-line/40 text-brand-muted cursor-not-allowed"
                                        } disabled:opacity-60`}
                                    >
                                        {addingId === r.id ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : isAvail ? (
                                            <Plus size={14} />
                                        ) : (
                                            <Lock size={14} />
                                        )}
                                        {addingId === r.id
                                            ? "Aggiungo…"
                                            : isAvail
                                                ? "Aggiungi al menu"
                                                : "Mancano ingredienti"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DialogContent>
            <MissingIngredientsDialog
                report={missingReport}
                onClose={() => { setMissingReport(null); onOpenChange(false); }}
            />
        </Dialog>
    );
}

function MissingIngredientsDialog({ report, onClose }) {
    const [adding, setAdding] = useState(false);
    if (!report) return null;
    const missing = report.missing_ingredients || [];

    const copyList = async () => {
        const lines = missing.map((m) => `• ${m.name}  (${m.needed_by_count} ricett${m.needed_by_count === 1 ? "a" : "e"})`);
        try {
            await navigator.clipboard.writeText(["Lista della spesa — ingredienti mancanti", "", ...lines].join("\n"));
            toast.success("Lista copiata negli appunti");
        } catch {
            toast.error("Impossibile copiare la lista");
        }
    };

    const addAllToList = async () => {
        setAdding(true);
        try {
            const items = missing.flatMap((m) =>
                (m.examples?.length > 0 ? m.examples : [null]).map((rn) => ({ name: m.name, recipe_name: rn || undefined }))
            );
            await api.post("/shopping-list/add", { items });
            toast.success(`${missing.length} ingredient${missing.length === 1 ? "e aggiunto" : "i aggiunti"} alla lista mancanti`);
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setAdding(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[80] bg-black/65 flex items-end sm:items-center justify-center p-0 sm:p-5"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl shadow-card overflow-hidden flex flex-col max-h-[90vh]"
                data-testid="missing-ingredients-dialog"
            >
                <div className="bg-brand-warning/8 border-b border-brand-warning/20 p-5 flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-brand-warning/15 text-brand-warning flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} />
                    </div>
                    <div className="flex-1">
                        <div className="font-display text-xl text-brand-ink">
                            {report.imported} ricette importate
                        </div>
                        <p className="mt-1 text-[13px] text-brand-muted">
                            <strong>{missing.length} ingredienti</strong> non sono presenti nella tua
                            dispensa. Aggiungili per sbloccare le ricette mancanti.
                        </p>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    <div className="eyebrow text-brand-ink/60 mb-3">Lista della spesa</div>
                    <ul className="space-y-2" data-testid="missing-ingredients-list">
                        {missing.map((m) => (
                            <li
                                key={m.name}
                                className="flex items-start gap-3 p-3 rounded-lg bg-brand-cream/50 border border-brand-line/60"
                            >
                                <span className="mt-0.5 h-2 w-2 rounded-full bg-brand-warning shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm text-brand-ink">{m.name}</div>
                                    <div className="text-[11px] text-brand-muted mt-0.5">
                                        Serve in {m.needed_by_count} ricett{m.needed_by_count === 1 ? "a" : "e"}
                                        {m.examples?.length > 0 && (
                                            <span className="italic"> — es. {m.examples.join(", ")}</span>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="border-t border-brand-line p-4 flex items-center justify-end gap-2 flex-wrap">
                    <button
                        onClick={copyList}
                        data-testid="missing-copy-btn"
                        className="px-4 py-2 rounded-full border border-brand-line text-sm font-semibold text-brand-muted hover:bg-brand-cream transition"
                    >
                        Copia lista
                    </button>
                    <button
                        onClick={addAllToList}
                        disabled={adding}
                        data-testid="missing-add-all-list-btn"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-warning text-white text-sm font-bold hover:opacity-90 transition disabled:opacity-60"
                    >
                        <ListPlus size={14} />
                        {adding ? "Aggiungo…" : `Metti tutti in lista (${missing.length})`}
                    </button>
                    <button
                        onClick={onClose}
                        data-testid="missing-close-btn"
                        className="px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navyLight transition"
                    >
                        Capito
                    </button>
                </div>
            </div>
        </div>
    );
}
